import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button, Logo, GradientBar, Card, Badge, PremiumBadge } from "@/components/design-system";
import { TID } from "@/lib/testIds";

const cn = (...xs) => xs.filter(Boolean).join(" ");

const Steps = [
  { n: "01", title: "Validate", body: "Submit your product, launch a structured feedback campaign, get matched with qualified reviewers." },
  { n: "02", title: "Improve", body: "Read insight reports clustered from real community feedback. Approve what to ship next — humans stay in charge." },
  { n: "03", title: "Launch", body: "Ship a public product page with proof of validation and a paid subscription in one click." },
  { n: "04", title: "Promote", body: "Verified Ambassadors get unique referral links tied to a 12-month recurring commission." },
  { n: "05", title: "Grow", body: "Ambassadors earn 12.5%, you keep 85%, platform 2.5% — automated end-to-end via Stripe Connect." },
];

export default function Landing() {
  const { user } = useAuth();
  const [annual, setAnnual] = React.useState(false);
  const [premiumAnnual, setPremiumAnnual] = React.useState(false);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const monthly = 10;
  const annualPerMonth = (monthly * 0.85).toFixed(2);
  const price = annual ? annualPerMonth : monthly.toFixed(2);

  return (
    <div className="min-h-screen bg-white text-[#0B1F3A]">
      {/* Nav */}
      <header data-testid={TID.landing.nav} className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#0B1F3A]/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between gap-2">
          <Link to="/" data-testid={TID.landing.logo} className="flex items-center gap-3 min-w-0">
            <Logo variant="light" height={44} className="md:!h-[60px]" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#how" className="hover:text-[#2D7FF9]">How it works</a>
            <a href="#audience" className="hover:text-[#2D7FF9]">Who it's for</a>
            <a href="#pricing" className="hover:text-[#2D7FF9]">Pricing</a>
            <a href="#ambassadors" className="hover:text-[#2D7FF9]">Ambassadors</a>
          </nav>
          <div className="flex items-center gap-2 md:gap-3">
            {user ? (
              <Link to="/app"><Button data-testid="landing-cta-open-app" variant="primary">Open app</Button></Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block"><Button data-testid={TID.landing.ctaLogin} variant="ghost">Log in</Button></Link>
                <Link to="/register"><Button data-testid={TID.landing.ctaRegister} variant="primary">Get started</Button></Link>
              </>
            )}
            <button
              type="button"
              aria-label="Open menu"
              data-testid="landing-mobile-menu-btn"
              onClick={() => setMobileNavOpen((v) => !v)}
              className="md:hidden -mr-1 p-2 rounded-lg text-[#0B1F3A] hover:bg-[#E8F1FF]"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {mobileNavOpen ? (<><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></>) : (<><line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="20" y2="17" /></>)}
              </svg>
            </button>
          </div>
        </div>
        {mobileNavOpen && (
          <div className="md:hidden border-t border-[#0B1F3A]/10 bg-white" data-testid="landing-mobile-menu">
            <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col text-sm font-medium">
              <a href="#how" onClick={() => setMobileNavOpen(false)} className="py-2.5 border-b border-[#0B1F3A]/5">How it works</a>
              <a href="#audience" onClick={() => setMobileNavOpen(false)} className="py-2.5 border-b border-[#0B1F3A]/5">Who it&apos;s for</a>
              <a href="#pricing" onClick={() => setMobileNavOpen(false)} className="py-2.5 border-b border-[#0B1F3A]/5">Pricing</a>
              <a href="#ambassadors" onClick={() => setMobileNavOpen(false)} className="py-2.5 border-b border-[#0B1F3A]/5">Ambassadors</a>
              {!user && (
                <Link to="/login" onClick={() => setMobileNavOpen(false)} className="py-2.5 text-[#2D7FF9]">Log in →</Link>
              )}
            </nav>
          </div>
        )}
        <GradientBar />
      </header>

      {/* Hero */}
      <section data-testid={TID.landing.hero} className="bg-[#E8F1FF]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#0B1F3A]/10 text-[10px] md:text-xs font-bold uppercase tracking-widest text-[#0B1F3A]/70">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7ED957]" />
              Now onboarding SaaS founders — EU launch
            </div>
            <h1 className="font-[Montserrat] font-extrabold text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tighter mt-6 text-[#0B1F3A]">
              Validate.<br/>Launch.<br/><span className="text-[#2D7FF9]">Grow.</span>
            </h1>
            <p className="mt-5 md:mt-6 text-base md:text-lg text-[#0B1F3A]/75 max-w-xl leading-relaxed">
              The AI-powered launch infrastructure for SaaS. Validate your product with qualified experts, recruit verified ambassadors, and acquire your first paying customers — in one continuous workflow.
            </p>
            <div className="mt-6 md:mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
              <Link to="/register"><Button data-testid={TID.landing.ctaHeroPrimary} variant="primary" className="w-full sm:w-auto px-8 py-4 text-base">Start validating — free</Button></Link>
              <a href="#how"><Button data-testid={TID.landing.ctaHeroSecondary} variant="secondary" className="w-full sm:w-auto px-8 py-4 text-base">See how it works</Button></a>
            </div>
            <p className="mt-4 text-xs text-[#0B1F3A]/55">No credit card. GDPR-first. EU data residency.</p>
          </div>

          {/* Loop diagram */}
          <div className="md:col-span-5">
            <div className="relative rounded-3xl bg-white p-8 border border-[#0B1F3A]/10 shadow-[0_10px_40px_rgba(11,31,58,0.08)]">
              <GradientBar className="rounded-t-3xl absolute top-0 left-0 right-0" />
              <div className="mt-2 flex items-center justify-between text-xs font-bold uppercase tracking-widest text-[#0B1F3A]/60">
                <span>The Launch Loop</span>
                <span className="text-[#2D7FF9]">v0.1</span>
              </div>
              <div className="mt-6 grid grid-cols-5 gap-3">
                {["Validate","Improve","Launch","Promote","Grow"].map((label, i) => (
                  <div key={label} className="flex flex-col items-center">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm text-white",
                      i === 0 && "bg-[#0B1F3A]",
                      i === 1 && "bg-[#2360D0]",
                      i === 2 && "bg-[#2D7FF9]",
                      i === 3 && "bg-[#20C7E8]",
                      i === 4 && "bg-[#7ED957] text-[#0B1F3A]",
                    )}>{i+1}</div>
                    <span className="mt-2 text-[11px] font-bold text-[#0B1F3A] text-center">{label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-[#0B1F3A]/10 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="font-[Montserrat] font-extrabold text-2xl text-[#0B1F3A]">85%</div>
                  <div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">Founder</div>
                </div>
                <div>
                  <div className="font-[Montserrat] font-extrabold text-2xl text-[#0B1F3A]">12.5%</div>
                  <div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">Ambassador</div>
                </div>
                <div>
                  <div className="font-[Montserrat] font-extrabold text-2xl text-[#0B1F3A]">12 mo</div>
                  <div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">Recurring</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audience */}
      <section id="audience" className="max-w-7xl mx-auto px-6 py-24">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#0B1F3A]/60">Built for three sides of the loop</p>
        <h2 className="font-[Montserrat] font-bold text-4xl md:text-5xl tracking-tight mt-3 max-w-3xl">One platform, three roles — and a single fair economy.</h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card data-testid={TID.landing.audienceFounder} className="hover:shadow-lg transition-shadow">
            <Badge variant="founder">Founder</Badge>
            <h3 className="font-[Montserrat] font-bold text-2xl mt-4">Validate. Launch. Grow with Ambassadors.</h3>
            <p className="mt-3 text-[#0B1F3A]/75 text-sm leading-relaxed">Run structured validation campaigns, then unlock <b>Verified Ambassadors</b> who promote your product on commission — <b>12.5% for 12 months</b>, only on subscribers they bring in.</p>
            <ul className="mt-5 space-y-2 text-sm text-[#0B1F3A]/80">
              <li className="flex gap-2"><span className="text-[#2D7FF9]">→</span> Qualified reviewer matching</li>
              <li className="flex gap-2"><span className="text-[#2D7FF9]">→</span> Verified Ambassadors amplify your launch</li>
              <li className="flex gap-2"><span className="text-[#2D7FF9]">→</span> Pay only on real referred subscriptions</li>
            </ul>
          </Card>
          <Card data-testid={TID.landing.audienceCommunity} className="hover:shadow-lg transition-shadow">
            <Badge variant="community">Community</Badge>
            <h3 className="font-[Montserrat] font-bold text-2xl mt-4">Give feedback, build reputation</h3>
            <p className="mt-3 text-[#0B1F3A]/75 text-sm leading-relaxed">Start as an Explorer, become a Validator, then a Verified Ambassador. Every helpful review counts — and the formula is transparent.</p>
            <ul className="mt-5 space-y-2 text-sm text-[#0B1F3A]/80">
              <li className="flex gap-2"><span className="text-[#2D7FF9]">→</span> Structured feedback templates</li>
              <li className="flex gap-2"><span className="text-[#2D7FF9]">→</span> Reputation score you can see</li>
              <li className="flex gap-2"><span className="text-[#2D7FF9]">→</span> Path to Verified Ambassador</li>
            </ul>
          </Card>
          <Card data-testid={TID.landing.audienceAmbassador} className="hover:shadow-lg transition-shadow relative overflow-hidden">
            <Badge variant="ambassador">Ambassador</Badge>
            <h3 className="font-[Montserrat] font-bold text-2xl mt-4">Earn 12.5% for a full year</h3>
            <p className="mt-3 text-[#0B1F3A]/75 text-sm leading-relaxed">Verified Ambassadors receive unique referral links and recurring commissions on every paying subscriber they bring in — automatically split via Stripe Connect.</p>
            <ul className="mt-5 space-y-2 text-sm text-[#0B1F3A]/80">
              <li className="flex gap-2"><span className="text-[#2D7FF9]">→</span> Automated payouts</li>
              <li className="flex gap-2"><span className="text-[#2D7FF9]">→</span> 12-month commission window</li>
              <li className="flex gap-2"><span className="text-[#2D7FF9]">→</span> Public ambassador profile (SEO)</li>
            </ul>
            <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full" style={{background:"linear-gradient(135deg,#20C7E8 0%,#7ED957 100%)",opacity:0.15}}/>
          </Card>
        </div>
      </section>

      {/* How it works */}
      <section id="how" data-testid={TID.landing.steps} className="bg-[#E8F1FF]/60 border-y border-[#0B1F3A]/10">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#0B1F3A]/60">How it works</p>
          <h2 className="font-[Montserrat] font-bold text-4xl md:text-5xl tracking-tight mt-3 max-w-3xl">Five steps, one continuous workflow.</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-6">
            {Steps.map((s) => (
              <Card key={s.n} className="!p-6">
                <div className="text-[11px] font-bold tracking-widest text-[#2D7FF9]">{s.n}</div>
                <h3 className="font-[Montserrat] font-bold text-xl mt-2">{s.title}</h3>
                <p className="mt-2 text-sm text-[#0B1F3A]/75 leading-relaxed">{s.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        {/* Beta banner across the pricing section */}
        <div
          className="mb-10 rounded-2xl border-2 border-[#7ED957] bg-[#7ED957]/15 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3"
          data-testid="landing-pricing-beta-banner"
        >
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#7ED957] text-[#0B1F3A] font-black text-sm shrink-0">β</span>
          <div className="flex-1">
            <p className="font-[Montserrat] font-bold text-base text-[#0B1F3A]">Beta program — all subscriptions are temporarily free.</p>
            <p className="text-sm text-[#0B1F3A]/75 mt-0.5">While we finish the private Beta, every founder & premium plan runs at zero cost. We'll email you 14 days before Beta ends.</p>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-white px-2.5 py-1 rounded-full text-[#0B1F3A]">Live · Beta</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Community Standard — Free forever */}
          <Card data-testid="landing-pricing-community-standard" className="relative !p-8 border-2 border-[#0B1F3A]/10 hover:shadow-lg transition-shadow">
            <div className="absolute -top-3 left-6 bg-[#0B1F3A] text-white font-bold text-[11px] uppercase tracking-widest px-3 py-1 rounded-full">Free forever</div>
            <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#7ED957]/20 border border-[#7ED957]/50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#0B1F3A]" data-testid="landing-pricing-community-standard-beta-tag">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7ED957] animate-pulse" />Beta · free
            </div>
            <Badge variant="community">Community · Standard</Badge>
            <h3 className="font-[Montserrat] font-extrabold text-2xl mt-4 text-[#0B1F3A]">Free forever</h3>
            <p className="mt-2 text-sm text-[#0B1F3A]/70">Join the community, give feedback, build reputation. No card. No credit. No expiry.</p>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-[Montserrat] font-extrabold text-5xl text-[#0B1F3A]">$0</span>
              <span className="text-[#0B1F3A]/60 text-sm">/ forever</span>
            </div>
            <ul className="mt-6 space-y-2 text-sm text-[#0B1F3A]/85">
              {[
                "Get early access to the newest apps in the market",
                "Submit structured feedback on every open validation campaign",
                "Earn reputation on every review — Explorer → Validator → Ambassador",
                "Track your standing on the public leaderboard",
                "Apply to become a Verified Ambassador and earn 12.5% recurring via our Referrals system",
              ].map((f) => (
                <li key={f} className="flex gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#7ED957] shrink-0" /><span>{f}</span></li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/register?role=community">
                <Button data-testid="landing-pricing-community-standard-cta" variant="secondary" className="w-full py-3.5">Join free</Button>
              </Link>
            </div>
          </Card>

          {/* Founder Launch — 7-day trial */}
          <Card data-testid={TID.landing.pricingCard} className="relative !p-0 border-0 overflow-hidden shadow-[0_10px_40px_rgba(11,31,58,0.15)]" style={{ background: "#0B1F3A", color: "#FFFFFF" }}>
            <GradientBar />
            <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#7ED957]/25 border border-[#7ED957]/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white z-10" data-testid="landing-pricing-founder-beta-tag">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7ED957] animate-pulse" />Beta · free
            </div>
            <div className="p-8">
              <div className="inline-flex items-center gap-2 rounded-md bg-[#2D7FF9]/25 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white" data-testid="landing-pricing-founder-trial-badge">
                7-day free trial
              </div>
              <div className="mt-4">
                <Badge variant="founder">Founder · Launch</Badge>
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-[Montserrat] font-extrabold text-5xl tracking-tighter text-white">${price}</span>
                <span className="text-white/70 text-sm">USD / month{annual ? " · billed annually" : ""}</span>
              </div>
              <div className="mt-3 inline-flex items-center gap-3 bg-white/10 border border-white/15 rounded-full p-1">
                <button
                  type="button"
                  onClick={() => setAnnual(false)}
                  className={cn("px-3 py-1 rounded-full text-xs font-semibold transition-colors", !annual ? "bg-white text-[#0B1F3A]" : "text-white/70")}
                >Monthly</button>
                <button
                  data-testid={TID.landing.pricingAnnualToggle}
                  type="button"
                  onClick={() => setAnnual(true)}
                  className={cn("px-3 py-1 rounded-full text-xs font-semibold transition-colors flex items-center gap-1.5", annual ? "bg-white text-[#0B1F3A]" : "text-white/70")}
                >Annual <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#7ED957]/25 text-white">−15%</span></button>
              </div>
              <p className="mt-4 text-white/80 text-sm">Everything you need to validate, launch, and grow — plus the 85% referral revenue share.</p>
              <ul className="mt-5 space-y-2 text-sm">
                {[
                  "Unlimited product listings and validation campaigns",
                  "Get Community Feedback reports about your products",
                  "Invite Verified Ambassadors directly to promote your product",
                  "Grow through our Referrals system — 12-month attribution + 85% revenue share",
                ].map((f) => (
                  <li key={f} className="flex gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#7ED957] shrink-0" /><span className="text-white/90">{f}</span></li>
                ))}
              </ul>
              <div className="mt-7">
                <Link to="/register?role=founder">
                  <Button data-testid={TID.landing.pricingCtaFounder} variant="onNavy" className="w-full px-8 py-3.5 text-base">Start 7-day free trial</Button>
                </Link>
                <p className="mt-2 text-[11px] text-white/60 text-center">No charge during Beta · cancel any time</p>
              </div>
            </div>
          </Card>

          {/* Community Premium */}
          <Card data-testid="landing-pricing-community-premium" className="relative !p-8 border-2 !border-[#20C7E8] hover:shadow-lg transition-shadow">
            <div className="absolute -top-3 left-6"><PremiumBadge /></div>
            <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#7ED957]/20 border border-[#7ED957]/50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#0B1F3A]" data-testid="landing-pricing-community-premium-beta-tag">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7ED957] animate-pulse" />Beta · free
            </div>
            <Badge variant="community">Community · Premium</Badge>
            <h3 className="font-[Montserrat] font-extrabold text-2xl mt-4 text-[#0B1F3A]">Stand out & earn faster</h3>
            <p className="mt-2 text-sm text-[#0B1F3A]/70">Reputation boosts, leaderboard priority, and first pick of new campaigns to review.</p>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-[Montserrat] font-extrabold text-5xl text-[#0B1F3A]">
                ${premiumAnnual ? "102" : "10"}
              </span>
              <span className="text-[#0B1F3A]/60 text-sm">/ {premiumAnnual ? "year" : "month"}</span>
            </div>
            <div className="mt-3 inline-flex items-center gap-2 bg-[#0B1F3A]/5 border border-[#0B1F3A]/10 rounded-full p-1">
              <button
                type="button"
                onClick={() => setPremiumAnnual(false)}
                data-testid="landing-pricing-premium-monthly-btn"
                className={cn("px-3 py-1 rounded-full text-xs font-semibold transition-colors", !premiumAnnual ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/70")}
              >Monthly</button>
              <button
                type="button"
                onClick={() => setPremiumAnnual(true)}
                data-testid="landing-pricing-premium-annual-btn"
                className={cn("px-3 py-1 rounded-full text-xs font-semibold transition-colors flex items-center gap-1.5", premiumAnnual ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/70")}
              >Annual <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#7ED957]/25 text-[#0B1F3A]">−15%</span></button>
            </div>
            <ul className="mt-6 space-y-2 text-sm text-[#0B1F3A]/85">
              {[
                "+25 reputation on activation, +5 on every renewal",
                "Leaderboard boost — Premium members surface first",
                "First access to new campaigns before they open publicly",
                "Recommendation boost — your feedback ranks higher for founders",
                "Priority shortlisting when founders search for ambassadors",
              ].map((f) => (
                <li key={f} className="flex gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#20C7E8] shrink-0" /><span>{f}</span></li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/register?role=community">
                <Button data-testid="landing-pricing-community-premium-cta" variant="primary" className="w-full py-3.5">
                  {premiumAnnual ? "Get Premium — annual" : "Get Premium — monthly"}
                </Button>
              </Link>
              <p className="mt-2 text-[11px] text-[#0B1F3A]/50 text-center">No charge during Beta · cancel any time</p>
            </div>
          </Card>

        </div>

        <div className="mt-12 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#0B1F3A]/60">Pricing</p>
          <h2 className="font-[Montserrat] font-bold text-3xl md:text-4xl tracking-tight mt-3 max-w-3xl mx-auto">One economy, three roles — no feedback paywalls, no dark patterns.</h2>
        </div>
      </section>

      {/* Ambassador CTA */}
      <section id="ambassadors" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="rounded-3xl p-10 md:p-14 border border-[#0B1F3A]/10 bg-white grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <Badge variant="ambassador">Ambassador program</Badge>
            <h3 className="font-[Montserrat] font-bold text-3xl md:text-4xl mt-4 tracking-tight">Get paid 12.5% for a full year of every subscription you bring in.</h3>
            <p className="mt-4 text-[#0B1F3A]/75">Prove yourself with quality feedback, get verified, unlock unique referral links, and earn recurring — paid automatically via Stripe Connect.</p>
            <div className="mt-6">
              <Link to="/register?role=community"><Button data-testid={TID.landing.ctaAmbassador} variant="primary" className="px-8 py-4 text-base">Join the community</Button></Link>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-2xl p-6 bg-[#E8F1FF]">
              <div className="font-[Montserrat] font-extrabold text-3xl text-[#0B1F3A]">12.5%</div>
              <div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60 mt-1">per subscription</div>
            </div>
            <div className="rounded-2xl p-6 bg-[#E8F1FF]">
              <div className="font-[Montserrat] font-extrabold text-3xl text-[#0B1F3A]">12 mo</div>
              <div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60 mt-1">recurring window</div>
            </div>
            <div className="rounded-2xl p-6 bg-[#E8F1FF]">
              <div className="font-[Montserrat] font-extrabold text-3xl text-[#0B1F3A]">Auto</div>
              <div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60 mt-1">Stripe payout</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid={TID.landing.footer} className="bg-[#0B1F3A] text-white">
        <GradientBar />
        <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <Logo variant="dark" height={112} />
            <p className="mt-5 text-white/70 text-sm max-w-xs">Validate. Launch. Grow. AI-powered launch infrastructure for SaaS — a product of <a href="https://lab.pastoria.es/" target="_blank" rel="noreferrer" data-testid="footer-pastoria-lab-link" className="font-semibold text-white hover:text-[#20C7E8] underline underline-offset-2">PASTORIA LAB</a>.</p>
          </div>
          <div>
            <h4 className="text-sm font-bold mb-4 text-white">Product</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><a href="#how" className="hover:text-[#20C7E8]">How it works</a></li>
              <li><a href="#pricing" className="hover:text-[#20C7E8]">Pricing</a></li>
              <li><a href="#ambassadors" className="hover:text-[#20C7E8]">Ambassadors</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-bold mb-4 text-white">Account</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/login" className="hover:text-[#20C7E8]">Log in</Link></li>
              <li><Link to="/register" className="hover:text-[#20C7E8]">Sign up</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-bold mb-4 text-white">Legal & GDPR</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/legal/privacy" className="hover:text-[#20C7E8]">Privacy</Link></li>
              <li><Link to="/legal/terms" className="hover:text-[#20C7E8]">Terms</Link></li>
              <li><Link to="/legal/cookies" className="hover:text-[#20C7E8]" data-testid="footer-cookies-link">Cookies</Link></li>
              <li><button type="button" onClick={() => window.dispatchEvent(new CustomEvent("sll:cookie-settings"))} className="hover:text-[#20C7E8] text-left" data-testid="footer-cookie-settings">Cookie preferences</button></li>
              <li><Link to="/app/settings" className="hover:text-[#20C7E8]">Data export & erasure</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/60">
            <span>© {new Date().getFullYear()} Pastoria Project SL · CIF B57664179 · Palma, Illes Balears · EU data residency</span>
            <span>Validate. Launch. Grow.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
